import React, { useEffect, useState } from 'react';

import { useRouteMatch } from 'react-router';
import { toast } from 'sonner';

import { NexoyaPortfolioParentContent } from '../../../../types';

import { useUpdatePortfolioContentBudgetBoundaries } from '../../../../graphql/portfolio/mutationUpdatePortfolioContentBudgetBoundaries';
import { useUpdatePortfolioContentTargetBoundaries } from '../../../../graphql/portfolio/mutationUpdatePortfolioContentTargetBoundaries';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components-ui/Dialog';
import { Input } from '../../../../components-ui/Input';
import { cn } from '../../../../lib/utils';

import Button from '../../../../components/Button';
import ButtonAsync from '../../../../components/ButtonAsync';
import { currencySymbol, useCurrencyStore } from '../../../../store/currency-selection';

import { isRoasBiddingStrategy } from './contentLimits';

type Props = {
  isOpen: boolean;
  toggleDialog: () => void;
  parentContent: NexoyaPortfolioParentContent;
};

function parseAmount(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = parseFloat(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

function toFieldValue(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/** Adornment shown inside a limit field, e.g. the currency symbol or a percent sign. */
function FieldUnit({ children }: { children: React.ReactNode }) {
  return (
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-300">
      {children}
    </span>
  );
}

export function ContentManageLimitsDialog({ parentContent, isOpen, toggleDialog }: Props) {
  const match = useRouteMatch();
  const portfolioId = parseInt(match.params.portfolioID, 10);
  const { currency } = useCurrencyStore();

  const existingBudgetMin = parentContent?.budgetMin ?? null;
  const existingBudgetMax = parentContent?.budgetMax ?? null;
  const existingMinRoasPercent = parentContent?.minTargetRoasPercent ?? null;
  const biddingStrategyType = parentContent?.content?.biddingStrategy?.type;

  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [minRoasPercent, setMinRoasPercent] = useState('');

  // Seed on open only: every successful save refetches, and that must not wipe unsaved edits.
  // Trade-off: a concurrent external edit is not pulled in, so a stale save can overwrite it.
  useEffect(() => {
    if (!isOpen) return;
    setBudgetMin(toFieldValue(existingBudgetMin));
    setBudgetMax(toFieldValue(existingBudgetMax));
    setMinRoasPercent(toFieldValue(existingMinRoasPercent));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, parentContent?.portfolioContentId]);

  const parsedBudgetMin = parseAmount(budgetMin);
  const parsedBudgetMax = parseAmount(budgetMax);
  const parsedMinRoasPercent = parseAmount(minRoasPercent);

  const budgetMinError =
    parsedBudgetMin !== null && parsedBudgetMin < 0 ? 'The minimum budget cannot be negative.' : null;
  const budgetMaxError =
    parsedBudgetMax !== null && parsedBudgetMax < 0 ? 'The maximum budget cannot be negative.' : null;
  // equal values are allowed: they pin the budget so the optimizer cannot change it
  const budgetRangeError =
    parsedBudgetMin !== null && parsedBudgetMax !== null && parsedBudgetMin > parsedBudgetMax
      ? 'The minimum budget cannot be higher than the maximum.'
      : null;
  const minRoasPercentError =
    parsedMinRoasPercent !== null && parsedMinRoasPercent <= 0
      ? 'The minimum target ROAS must be greater than zero.'
      : null;

  const budgetChanged = parsedBudgetMin !== existingBudgetMin || parsedBudgetMax !== existingBudgetMax;
  const targetChanged = parsedMinRoasPercent !== existingMinRoasPercent;

  // The budget mutation overwrites both columns, so always send the complete pair.
  const [updateBudgetBoundaries, { loading: budgetLoading }] = useUpdatePortfolioContentBudgetBoundaries({
    portfolioId,
    contents: [parentContent.portfolioContentId],
    newBudget: { min: parsedBudgetMin, max: parsedBudgetMax },
  });
  const [updateTargetBoundaries, { loading: targetLoading }] = useUpdatePortfolioContentTargetBoundaries({
    portfolioId,
    contents: [parentContent.portfolioContentId],
    newTarget: { minRoasPercent: parsedMinRoasPercent },
  });

  const loading = budgetLoading || targetLoading;
  const hasError = !!budgetMinError || !!budgetMaxError || !!budgetRangeError || !!minRoasPercentError;
  const canSubmit = !loading && !hasError && (budgetChanged || targetChanged);

  const handleSubmit = async () => {
    const pending: { label: string; run: () => Promise<unknown> }[] = [];
    if (budgetChanged) pending.push({ label: 'Budget limits', run: () => updateBudgetBoundaries() });
    if (targetChanged) pending.push({ label: 'Target limits', run: () => updateTargetBoundaries() });

    const results = await Promise.allSettled(pending.map(({ run }) => run()));
    const failed = pending
      .map((section, i) => ({ ...section, result: results[i] }))
      .filter(({ result }) => result.status === 'rejected');

    if (failed.length === 0) {
      toast.success('Content limits updated');
      toggleDialog();
      return;
    }

    // Report exactly what did not land — the other section may already have been saved.
    failed.forEach(({ result }) => {
      console.error((result as PromiseRejectedResult).reason);
    });
    toast.error(
      failed.length === pending.length
        ? 'Could not update the content limits'
        : `${failed[0].label} could not be saved. The other limits were updated.`,
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(next) => !next && toggleDialog()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage content limits</DialogTitle>
          {/* DialogContent is a grid, so this item's min-content width sizes the track. `break-words`
              wraps the title but still reports the full token width, widening the whole dialog;
              `anywhere` shrinks min-content as well. */}
          <DialogDescription className="pt-2 [overflow-wrap:anywhere]">
            Constrain how the optimizer allocates budget to{' '}
            <span className="font-medium text-neutral-500">{parentContent?.content?.title}</span>. Leave a field empty
            for no limit.
          </DialogDescription>
        </DialogHeader>

        <section>
          <h3 className="mb-3 text-[11px] font-semibold uppercase text-neutral-400">Budget</h3>
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="budget-min-input" className="mb-1.5 block text-sm text-neutral-500">
                Minimum
              </label>
              <div className="relative">
                <Input
                  id="budget-min-input"
                  type="number"
                  step={0.01}
                  value={budgetMin}
                  placeholder="No limit"
                  onChange={(e) => setBudgetMin(e.currentTarget.value)}
                  aria-invalid={!!budgetMinError || !!budgetRangeError}
                  className={cn('pr-10', (budgetMinError || budgetRangeError) && 'border-red-400')}
                />
                <FieldUnit>{currencySymbol[currency]}</FieldUnit>
              </div>
            </div>
            <div className="flex-1">
              <label htmlFor="budget-max-input" className="mb-1.5 block text-sm text-neutral-500">
                Maximum
              </label>
              <div className="relative">
                <Input
                  id="budget-max-input"
                  type="number"
                  step={0.01}
                  value={budgetMax}
                  placeholder="No limit"
                  onChange={(e) => setBudgetMax(e.currentTarget.value)}
                  aria-invalid={!!budgetMaxError || !!budgetRangeError}
                  className={cn('pr-10', (budgetMaxError || budgetRangeError) && 'border-red-400')}
                />
                <FieldUnit>{currencySymbol[currency]}</FieldUnit>
              </div>
            </div>
          </div>
          {budgetMinError || budgetMaxError || budgetRangeError ? (
            <p role="alert" className="mt-2 text-xs text-red-400">
              {budgetMinError ?? budgetMaxError ?? budgetRangeError}
            </p>
          ) : null}
        </section>

        <section>
          <h3 className="mb-3 text-[11px] font-semibold uppercase text-neutral-400">Performance</h3>
          <label htmlFor="min-target-roas-input" className="mb-1.5 block text-sm text-neutral-500">
            Minimum target ROAS
          </label>
          <div className="relative">
            <Input
              id="min-target-roas-input"
              type="number"
              step={1}
              value={minRoasPercent}
              placeholder="No limit"
              onChange={(e) => setMinRoasPercent(e.currentTarget.value)}
              aria-invalid={!!minRoasPercentError}
              className={cn('pr-10', minRoasPercentError && 'border-red-400')}
            />
            <FieldUnit>%</FieldUnit>
          </div>
          {minRoasPercentError ? (
            <p role="alert" className="mt-2 text-xs text-red-400">
              {minRoasPercentError}
            </p>
          ) : (
            <p className="mt-2 text-xs text-neutral-400">400% means a ROAS of 4x.</p>
          )}
          {!isRoasBiddingStrategy(biddingStrategyType) ? (
            <div className="mt-3 rounded-md bg-yellow-50 p-3 text-sm text-neutral-500">
              {biddingStrategyType ? (
                <>
                  This content uses the <span className="font-medium">{biddingStrategyType}</span> bidding
                  strategy.{' '}
                </>
              ) : (
                'No bidding strategy is reported for this content. '
              )}
              A ROAS limit is saved, but only takes effect once the campaign runs a ROAS-based bidding strategy.
            </div>
          ) : null}
        </section>

        <DialogFooter>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              toggleDialog();
            }}
            disabled={loading}
            variant="contained"
            color="secondary"
            size="small"
          >
            Cancel
          </Button>
          <ButtonAsync
            disabled={!canSubmit}
            loading={loading}
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            size="small"
            autoFocus
          >
            Save limits
          </ButtonAsync>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
